import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import config from 'configs/app';
import { useScrollDirection } from 'lib/contexts/scrollDirection';
import RewardsButton from 'ui/rewards/RewardsButton';
import NetworkLogo from 'ui/snippets/networkMenu/NetworkLogo';
import SearchBar from 'ui/snippets/searchBar/SearchBar';
import UserProfileMobile from 'ui/snippets/user/profile/UserProfileMobile';
import UserWalletMobile from 'ui/snippets/user/wallet/UserWalletMobile';

import Burger, { BurgerDropdownContent } from './Burger';

type Props = {
  hideSearchBar?: boolean;
  renderSearchBar?: () => React.ReactNode;
};

const HeaderMobile = ({ hideSearchBar, renderSearchBar }: Props) => {
  const bgColor = useColorModeValue('white', 'black');
  const scrollDirection = useScrollDirection();
  const { ref, inView } = useInView({ threshold: 1 });
  const [ isNavOpen, setIsNavOpen ] = React.useState(false);
  const navDropdownRef = React.useRef<HTMLDivElement>(null);

  const searchBar = renderSearchBar ? renderSearchBar() : <SearchBar/>;

  const handleNavToggle = React.useCallback(() => {
    setIsNavOpen((prev) => !prev);
  }, []);

  const handleNavClose = React.useCallback(() => {
    setIsNavOpen(false);
  }, []);

  React.useEffect(() => {
    if (!isNavOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(event.target as Node)) {
        setIsNavOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ isNavOpen ]);

  return (
    <Box
      ref={ ref }
      bgColor={ bgColor }
      display={{ base: 'block', lg: 'none' }}
      position="sticky"
      top="-1px"
      left={ 0 }
      zIndex="sticky2"
      pt="1px"
    >
      <Box ref={ navDropdownRef } position="relative">
        <Flex
          as="header"
          paddingX={ 2 }
          paddingY={ 2 }
          bgColor={ bgColor }
          width="100%"
          alignItems="center"
          transitionProperty="box-shadow"
          transitionDuration="slow"
          boxShadow={ !inView && scrollDirection === 'down' ? 'md' : 'none' }
        >
          <NetworkLogo ml={ 2 } mr="auto"/>
          <Flex columnGap={ 2 } alignItems="center" ml="auto">
            { config.features.rewards.isEnabled && <RewardsButton/> }
            {
              (config.features.account.isEnabled && <UserProfileMobile/>) ||
              (config.features.blockchainInteraction.isEnabled && <UserWalletMobile/>) ||
              <Box boxSize={ 10 }/>
            }
            <Box ml="auto">
              <Burger isOpen={ isNavOpen } onToggle={ handleNavToggle }/>
            </Box>
          </Flex>
        </Flex>
        { isNavOpen && (
          <Box
            position="absolute"
            top="100%"
            left={ 0 }
            right={ 0 }
            zIndex="dropdown"
            bg="card_bg"
            borderBottomWidth="1px"          
            borderColor="card_border"
            maxHeight="85vh"
            overflowY="auto"
            boxShadow="sm"
            px={ 3 }
            pb={ 3 }
            display="flex"
            flexDirection="column"
          >
            <BurgerDropdownContent onClose={ handleNavClose }/>
          </Box>
        ) }
      </Box>
      { !hideSearchBar && searchBar }
    </Box>
  );
};

export default React.memo(HeaderMobile);
